"use client";

import { publicPhotoUrl } from "@/lib/storage-url";
import { DemoBadge } from "@/components/feed/demo-badge";
import type { LightboxPhoto } from "@/components/feed/photo-lightbox";
import plantsData from "@/data/plants.json";
import type { Plant } from "@/types/plants";

const plants = plantsData as Plant[];
const plantsMap: Record<string, Plant> = {};
for (const p of plants) plantsMap[p.id] = p;

interface EventCardProps {
  event: {
    id: string;
    type: string;
    data: Record<string, unknown>;
    created_at: string;
  };
  gardenName: string;
  gardenId: string;
  firstName: string | null;
  isDemo?: boolean;
  onPhotoClick?: (photos: LightboxPhoto[], index: number) => void;
}

/**
 * "3.9" + "kg" used to render as "3.9kg", and "unit" — the database code —
 * leaked straight into the sentence as "24unit". Quantities are French text
 * here, not raw columns.
 */
function formatQuantity(quantity: number, unit?: string): string {
  const value = quantity
    .toFixed(unit === "kg" ? 1 : 0)
    .replace(/\.0$/, "")
    .replace(".", ",");
  if (unit === "unit") return `${value} ${quantity > 1 ? "unités" : "unité"}`;
  return `${value} ${unit ?? ""}`.trim();
}

function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `il y a ${days}j`;
  return `il y a ${Math.floor(days / 30)} mois`;
}

type EventPhoto = {
  id?: string;
  storage_path: string;
  note?: string | null;
  plant_id?: string | null;
};

function PhotoTile({
  photo,
  alt,
  onClick,
  className = "",
}: {
  photo: EventPhoto;
  alt: string;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`block bg-surface-container overflow-hidden ${className} ${onClick ? "cursor-zoom-in" : "cursor-default"}`}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={publicPhotoUrl(photo.storage_path)}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </button>
  );
}

function PhotoGallery({
  photos,
  alt,
  onPhotoClick,
}: {
  photos: EventPhoto[];
  alt: string;
  onPhotoClick?: (photos: LightboxPhoto[], index: number) => void;
}) {
  if (photos.length === 0) return null;

  const lightboxSet: LightboxPhoto[] = photos.map((p) => ({
    storage_path: p.storage_path,
    note: p.note,
  }));

  const openAt = onPhotoClick ? (idx: number) => onPhotoClick(lightboxSet, idx) : undefined;

  if (photos.length === 1) {
    return (
      <div className="rounded-xl overflow-hidden aspect-video">
        <PhotoTile
          photo={photos[0]}
          alt={alt}
          onClick={openAt ? () => openAt(0) : undefined}
          className="w-full h-full"
        />
      </div>
    );
  }

  if (photos.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-1 rounded-xl overflow-hidden aspect-video">
        {photos.map((p, i) => (
          <PhotoTile
            key={p.id ?? i}
            photo={p}
            alt={alt}
            onClick={openAt ? () => openAt(i) : undefined}
            className="w-full h-full"
          />
        ))}
      </div>
    );
  }

  if (photos.length === 3) {
    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-1 rounded-xl overflow-hidden aspect-video">
        <PhotoTile
          photo={photos[0]}
          alt={alt}
          onClick={openAt ? () => openAt(0) : undefined}
          className="row-span-2 h-full"
        />
        <PhotoTile
          photo={photos[1]}
          alt={alt}
          onClick={openAt ? () => openAt(1) : undefined}
          className="h-full"
        />
        <PhotoTile
          photo={photos[2]}
          alt={alt}
          onClick={openAt ? () => openAt(2) : undefined}
          className="h-full"
        />
      </div>
    );
  }

  if (photos.length === 4) {
    return (
      <div className="grid grid-cols-2 gap-1">
        {photos.map((p, i) => (
          <div key={p.id ?? i} className="aspect-square rounded-xl overflow-hidden">
            <PhotoTile
              photo={p}
              alt={alt}
              onClick={openAt ? () => openAt(i) : undefined}
              className="w-full h-full"
            />
          </div>
        ))}
      </div>
    );
  }

  // 5+ photos: masonry — 2 medium tiles on top, 3 smaller below.
  // If there are more than 5, the 5th tile is dimmed with a "+N photos"
  // overlay to invite a click into the lightbox.
  const top = photos.slice(0, 2);
  const bottom = photos.slice(2, 5);
  const extra = photos.length - 5;

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-2 gap-1">
        {top.map((p, i) => (
          <div key={p.id ?? i} className="aspect-[4/3] rounded-xl overflow-hidden">
            <PhotoTile
              photo={p}
              alt={alt}
              onClick={openAt ? () => openAt(i) : undefined}
              className="w-full h-full"
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-1">
        {bottom.map((p, i) => {
          const idx = i + 2;
          const isLast = i === bottom.length - 1 && extra > 0;
          return (
            <div key={p.id ?? idx} className="relative aspect-square rounded-xl overflow-hidden">
              <PhotoTile
                photo={p}
                alt={alt}
                onClick={openAt ? () => openAt(idx) : undefined}
                className="w-full h-full"
              />
              {isLast && (
                <button
                  type="button"
                  onClick={openAt ? () => openAt(idx) : undefined}
                  className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center leading-tight cursor-zoom-in"
                >
                  <span className="text-headline-sm text-white font-semibold">+{extra}</span>
                  <span className="text-label-md text-white/80">photos</span>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function EventCard({
  event,
  gardenName,
  gardenId,
  firstName,
  isDemo,
  onPhotoClick,
}: EventCardProps) {
  const name = firstName || "Un jardinier";
  const badge = isDemo ? <DemoBadge className="ml-2 align-middle" /> : null;
  const data = event.data;

  if (event.type === "photo_added" || event.type === "photos_added") {
    let photos: EventPhoto[] = [];
    let note: string | undefined;
    let plantId: string | undefined;

    if (event.type === "photos_added") {
      photos = (data.photos as EventPhoto[] | undefined) ?? [];
      note = (data.note as string | undefined) ?? photos.find((p) => p.note)?.note ?? undefined;
      plantId = (data.plant_id as string | undefined) ?? photos.find((p) => p.plant_id)?.plant_id ?? undefined;
    } else {
      const storagePath = data.storage_path as string | undefined;
      note = data.note as string | undefined;
      plantId = data.plant_id as string | undefined;
      if (storagePath) {
        photos = [{ storage_path: storagePath, note: note ?? null, plant_id: plantId ?? null }];
      }
    }

    const plant = plantId ? plantsMap[plantId] : null;
    const photoCount = photos.length;

    return (
      <div className="card space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">&#128248;</span>
          <div className="flex-1 min-w-0">
            <p className="text-label-lg text-on-surface">
              <span className="font-semibold">{name}</span>
              {photoCount > 1 ? ` a partagé ${photoCount} photos` : " a partagé une photo"}
              {" · "}
              <a href={`/galerie/${gardenId}`} className="text-primary hover:underline">{gardenName}</a>
              {badge}
            </p>
            <p className="text-label-md text-on-surface-variant">{timeAgo(event.created_at)}</p>
          </div>
          {plant && (
            <span className="px-2 py-0.5 rounded-lg bg-surface-container-high text-label-md">
              {plant.emoji} {plant.name}
            </span>
          )}
        </div>
        {note && <p className="text-body-md text-on-surface">{note}</p>}
        {photoCount > 0 && (
          <PhotoGallery photos={photos} alt={note ?? gardenName} onPhotoClick={onPhotoClick} />
        )}
      </div>
    );
  }

  if (event.type === "harvest_logged") {
    const plantId = data.plant_id as string | undefined;
    const plant = plantId ? plantsMap[plantId] : null;
    const variety = data.variety as string | undefined;
    const quantity = data.quantity as number | undefined;
    const unit = data.unit as string | undefined;
    const harvestPhotos = ((data.photos as string[] | undefined) ?? []).map((path) => ({
      storage_path: path,
    }));

    const displayName = variety
      ? `${plant?.emoji ?? ""} ${variety}`
      : plant
        ? `${plant.emoji} ${plant.name}`
        : "une récolte";

    return (
      <div className="card space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{plant?.emoji ?? "\u{1F33D}"}</span>
          <div className="flex-1 min-w-0">
            <p className="text-label-lg text-on-surface">
              <span className="font-semibold">{name}</span>
              {" a récolté "}
              {quantity != null && (
                <span className="font-semibold">
                  {formatQuantity(quantity, unit)}
                </span>
              )}
              {" de "}{displayName}
              {badge}
            </p>
            <p className="text-label-md text-on-surface-variant">
              <a href={`/galerie/${gardenId}`} className="text-primary hover:underline">{gardenName}</a>
              {" · "}{timeAgo(event.created_at)}
            </p>
          </div>
        </div>
        {harvestPhotos.length > 0 && (
          <PhotoGallery
            photos={harvestPhotos}
            alt={displayName}
            onPhotoClick={onPhotoClick}
          />
        )}
      </div>
    );
  }

  if (event.type === "garden_created") {
    return (
      <div className="card flex items-center gap-3">
        <span className="text-lg">&#127793;</span>
        <div>
          <p className="text-label-lg text-on-surface">
            <span className="font-semibold">{name}</span>
            {" a créé son potager "}
            <a href={`/galerie/${gardenId}`} className="text-primary hover:underline">{gardenName}</a>
            {badge}
          </p>
          <p className="text-label-md text-on-surface-variant">{timeAgo(event.created_at)}</p>
        </div>
      </div>
    );
  }

  return null;
}
